import { useCallback } from 'react';
import { getPostHog } from '../services/posthog';

// Event categories
export const EVENT_CATEGORIES = {
  NAVIGATION: 'Navigation',
  ENGAGEMENT: 'Engagement',
  CONVERSION: 'Conversion',
  AUTHENTICATION: 'Authentication',
  CONTENT: 'Content',
  TOOLS: 'Tools',
  ACCOUNT: 'Account',
  PERFORMANCE: 'Performance',
  PRICING: 'Pricing',
  SPORTS: 'Sports',
  PREMIUM_TIER: 'Premium Tier',
  FAIRLINE: 'FairLine',
  TAP_TO_BET: 'Tap to Bet'
};

export const useAnalytics = () => {
  // Base event tracking function
  const trackEvent = useCallback((eventName, properties = {}, options) => {
    try {
      // The snippet's stub queues calls made before the library loads.
      const posthog = getPostHog();
      if (posthog) {
        const enhancedProps = {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          pathname: window.location.pathname,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          is_mobile: window.innerWidth < 768,
          ...properties
        };

        if (options === undefined) {
          posthog.capture(eventName, enhancedProps);
        } else {
          posthog.capture(eventName, enhancedProps, options);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Analytics error:', error);
      return false;
    }
  }, []);

  // NAVIGATION EVENTS
  const trackPageView = useCallback((path, properties = {}) => {
    return trackEvent('page_view', {
      category: EVENT_CATEGORIES.NAVIGATION,
      path,
      ...properties
    });
  }, [trackEvent]);

  const trackNavigation = useCallback((destination, source, properties = {}) => {
    return trackEvent('navigation', {
      category: EVENT_CATEGORIES.NAVIGATION,
      destination,
      source,
      ...properties
    });
  }, [trackEvent]);

  // SPORTS EVENTS
  const trackSportPage = useCallback((sport, pageType, properties = {}) => {
    return trackEvent('sport_page_viewed', {
      category: EVENT_CATEGORIES.SPORTS,
      sport: sport.toUpperCase(),
      page_type: pageType,
      ...properties
    });
  }, [trackEvent]);

  // DASHBOARD EVENTS
  const trackDashboardLoaded = useCallback((loadTimeMs, properties = {}) => {
    return trackEvent('dashboard_loaded', {
      category: EVENT_CATEGORIES.PERFORMANCE,
      load_time_ms: loadTimeMs,
      ...properties
    });
  }, [trackEvent]);

  const trackDashboardError = useCallback((errorType, errorMessage, properties = {}) => {
    return trackEvent('dashboard_error', {
      category: EVENT_CATEGORIES.PERFORMANCE,
      error_type: errorType,
      error_message: errorMessage,
      ...properties
    });
  }, [trackEvent]);

  const trackFilterChanged = useCallback((filterType, oldValue, newValue, properties = {}) => {
    return trackEvent('filter_changed', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      filter_type: filterType,
      old_value: oldValue,
      new_value: newValue,
      ...properties
    });
  }, [trackEvent]);

  const trackFilterReset = useCallback((properties = {}) => {
    return trackEvent('filter_reset', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      ...properties
    });
  }, [trackEvent]);

  // PRICING EVENTS
  const trackPricingView = useCallback((planType, isAnnual, properties = {}) => {
    return trackEvent('pricing_plan_viewed', {
      category: EVENT_CATEGORIES.PRICING,
      plan_type: planType,
      billing_cycle: isAnnual ? 'annual' : 'monthly',
      ...properties
    });
  }, [trackEvent]);

  const trackPlanToggle = useCallback((oldCycle, newCycle, properties = {}) => {
    return trackEvent('pricing_cycle_toggled', {
      category: EVENT_CATEGORIES.PRICING,
      old_cycle: oldCycle,
      new_cycle: newCycle,
      ...properties
    });
  }, [trackEvent]);

  const trackSubscribeClick = useCallback((planType, isAnnual, properties = {}) => {
    return trackEvent('subscribe_button_clicked', {
      category: EVENT_CATEGORIES.CONVERSION,
      plan_type: planType,
      billing_cycle: isAnnual ? 'annual' : 'monthly',
      ...properties
    });
  }, [trackEvent]);

  const trackCheckoutStarted = useCallback((planType, isAnnual, amount, properties = {}) => {
    return trackEvent('checkout_started', {
      category: EVENT_CATEGORIES.CONVERSION,
      plan_type: planType,
      billing_cycle: isAnnual ? 'annual' : 'monthly',
      amount,
      ...properties
    });
  }, [trackEvent]);

  const trackCheckoutCompleted = useCallback((planType, isAnnual, amount, properties = {}) => {
    return trackEvent('checkout_completed', {
      category: EVENT_CATEGORIES.CONVERSION,
      plan_type: planType,
      billing_cycle: isAnnual ? 'annual' : 'monthly',
      amount,
      ...properties
    });
  }, [trackEvent]);

  // AUTHENTICATION EVENTS
  const trackLogin = useCallback((method, properties = {}) => {
    return trackEvent('user_login', {
      category: EVENT_CATEGORIES.AUTHENTICATION,
      method,
      ...properties
    });
  }, [trackEvent]);

  const trackSignup = useCallback((method, properties = {}) => {
    return trackEvent('user_signup', {
      category: EVENT_CATEGORIES.AUTHENTICATION,
      method,
      ...properties
    });
  }, [trackEvent]);

  const trackLogout = useCallback((properties = {}) => {
    return trackEvent('user_logout', {
      category: EVENT_CATEGORIES.AUTHENTICATION,
      ...properties
    });
  }, [trackEvent]);

  // CONTENT EVENTS
  const trackContentView = useCallback((contentType, contentId, properties = {}) => {
    return trackEvent('content_viewed', {
      category: EVENT_CATEGORIES.CONTENT,
      content_type: contentType,
      content_id: contentId,
      ...properties
    });
  }, [trackEvent]);

  const trackSportSelected = useCallback((sport, properties = {}) => {
    return trackEvent('sport_selected', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      sport,
      ...properties
    });
  }, [trackEvent]);

  // TOOLS EVENTS
  const trackToolUsed = useCallback((toolName, properties = {}) => {
    return trackEvent('tool_used', {
      category: EVENT_CATEGORIES.TOOLS,
      tool_name: toolName,
      ...properties
    });
  }, [trackEvent]);

  const trackToolEmailCaptured = useCallback((source, toolPath, properties = {}) => {
    return trackEvent('tool_email_captured', {
      category: EVENT_CATEGORIES.TOOLS,
      source,
      tool_path: toolPath,
      ...properties
    });
  }, [trackEvent]);

  // PREMIUM TIER EVENTS
  const trackTierStatusIdentified = useCallback((tier, previousTier, properties = {}) => {
    return trackEvent('tier_status_identified', {
      category: EVENT_CATEGORIES.PREMIUM_TIER,
      tier,
      previous_tier: previousTier,
      ...properties
    });
  }, [trackEvent]);

  const trackPremiumFeatureAccessed = useCallback((featureName, properties = {}) => {
    return trackEvent('premium_feature_accessed', {
      category: EVENT_CATEGORIES.PREMIUM_TIER,
      feature_name: featureName,
      ...properties
    });
  }, [trackEvent]);

  const trackPremiumFeatureBlocked = useCallback((featureName, blockedAction, properties = {}) => {
    return trackEvent('premium_feature_blocked', {
      category: EVENT_CATEGORIES.PREMIUM_TIER,
      feature_name: featureName,
      blocked_action: blockedAction,
      ...properties
    });
  }, [trackEvent]);

  const trackUpgradePromptViewed = useCallback((promptType, location, properties = {}) => {
    return trackEvent('upgrade_prompt_viewed', {
      category: EVENT_CATEGORIES.PREMIUM_TIER,
      prompt_type: promptType,
      location,
      ...properties
    });
  }, [trackEvent]);

  const trackUpgradePromptClicked = useCallback((promptType, location, properties = {}) => {
    return trackEvent('upgrade_prompt_clicked', {
      category: EVENT_CATEGORIES.CONVERSION,
      prompt_type: promptType,
      location,
      ...properties
    });
  }, [trackEvent]);

  // FAIRLINE EVENTS
  const trackFairlineColumnViewed = useCallback((sport, pageType, marketType, properties = {}) => {
    return trackEvent('fairline_column_viewed', {
      category: EVENT_CATEGORIES.FAIRLINE,
      sport: sport.toUpperCase(),
      page_type: pageType,
      market_type: marketType,
      ...properties
    });
  }, [trackEvent]);

  const trackFairlineTooltipViewed = useCallback((sport, pageType, tooltipContent, properties = {}) => {
    return trackEvent('fairline_tooltip_viewed', {
      category: EVENT_CATEGORIES.FAIRLINE,
      sport: sport.toUpperCase(),
      page_type: pageType,
      tooltip_content: tooltipContent,
      ...properties
    });
  }, [trackEvent]);

  const trackFairlineDataLoaded = useCallback((sport, pageType, dataPointsCount, loadTimeMs, properties = {}) => {
    return trackEvent('fairline_data_loaded', {
      category: EVENT_CATEGORIES.FAIRLINE,
      sport: sport.toUpperCase(),
      page_type: pageType,
      data_points_count: dataPointsCount,
      load_time_ms: loadTimeMs,
      ...properties
    });
  }, [trackEvent]);

  const trackEvOpportunityViewed = useCallback((sport, evPercentage, marketType, properties = {}) => {
    return trackEvent('ev_opportunity_viewed', {
      category: EVENT_CATEGORIES.FAIRLINE,
      sport: sport.toUpperCase(),
      ev_percentage: evPercentage,
      market_type: marketType,
      ...properties
    });
  }, [trackEvent]);

  // TAP TO BET EVENTS
  const trackTapToBetClicked = useCallback((bookmaker, evAtClick, betType, sport, properties = {}) => {
    return trackEvent('tap_to_bet_clicked', {
      category: EVENT_CATEGORIES.TAP_TO_BET,
      bookmaker: bookmaker.toLowerCase(),
      ev_at_click: evAtClick,
      bet_type: betType,
      sport: sport.toUpperCase(),
      ...properties
    });
  }, [trackEvent]);

  const trackTapToBetBlocked = useCallback((bookmaker, evAtClick, betType, sport, actionTaken, properties = {}) => {
    return trackEvent('tap_to_bet_blocked', {
      category: EVENT_CATEGORIES.TAP_TO_BET,
      bookmaker: bookmaker.toLowerCase(),
      ev_at_click: evAtClick,
      bet_type: betType,
      sport: sport.toUpperCase(),
      action_taken: actionTaken,
      ...properties
    });
  }, [trackEvent]);

  const trackTapToBetLoginRedirect = useCallback((bookmaker, evAtClick, betType, sport, returnPath, properties = {}) => {
    return trackEvent('tap_to_bet_login_redirect', {
      category: EVENT_CATEGORIES.TAP_TO_BET,
      bookmaker: bookmaker.toLowerCase(),
      ev_at_click: evAtClick,
      bet_type: betType,
      sport: sport.toUpperCase(),
      return_path: returnPath,
      ...properties
    });
  }, [trackEvent]);

  // TRACK RECORD EVENTS
  const trackTrackRecordViewed = useCallback((properties = {}) => {
    return trackEvent('track_record_viewed', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      ...properties
    });
  }, [trackEvent]);

  // WHY THIS PICK EVENTS
  const trackWhyThisPickExpanded = useCallback((sport, marketType, evPercent, bookmaker, properties = {}) => {
    return trackEvent('why_this_pick_expanded', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      sport: sport.toUpperCase(),
      market_type: marketType,
      ev_percent: evPercent,
      bookmaker,
      ...properties
    });
  }, [trackEvent]);

  // EXIT INTENT EVENTS
  const trackExitIntentShown = useCallback((properties = {}) => {
    return trackEvent('exit_intent_shown', {
      category: EVENT_CATEGORIES.CONVERSION,
      ...properties
    });
  }, [trackEvent]);

  const trackExitIntentCtaClicked = useCallback((properties = {}) => {
    return trackEvent('exit_intent_cta_clicked', {
      category: EVENT_CATEGORIES.CONVERSION,
      ...properties
    });
  }, [trackEvent]);

  // HOMEPAGE EVENTS
  const trackHomepageTrackRecordClicked = useCallback((properties = {}) => {
    return trackEvent('homepage_track_record_clicked', {
      category: EVENT_CATEGORIES.ENGAGEMENT,
      ...properties
    });
  }, [trackEvent]);

  // SPORTS EVENTS - Enhanced
  const trackBestPicksTabChanged = useCallback((sport, oldTab, newTab, tabName, properties = {}) => {
    return trackEvent('best_picks_tab_changed', {
      category: EVENT_CATEGORIES.SPORTS,
      sport: sport.toUpperCase(),
      old_tab: oldTab,
      new_tab: newTab,
      tab_name: tabName,
      ...properties
    });
  }, [trackEvent]);

  return {
    // Base tracking
    trackEvent,
    
    // Navigation
    trackPageView,
    trackNavigation,
    
    // Sports
    trackSportPage,
    trackBestPicksTabChanged,
    
    // Dashboard
    trackDashboardLoaded,
    trackDashboardError,
    trackFilterChanged,
    trackFilterReset,
    
    // Pricing
    trackPricingView,
    trackPlanToggle,
    trackSubscribeClick,
    trackCheckoutStarted,
    trackCheckoutCompleted,
    
    // Authentication
    trackLogin,
    trackSignup,
    trackLogout,
    
    // Content
    trackContentView,
    trackSportSelected,
    
    // Tools
    trackToolUsed,
    trackToolEmailCaptured,
    
    // Premium Tier
    trackTierStatusIdentified,
    trackPremiumFeatureAccessed,
    trackPremiumFeatureBlocked,
    trackUpgradePromptViewed,
    trackUpgradePromptClicked,
    
    // FairLine
    trackFairlineColumnViewed,
    trackFairlineTooltipViewed,
    trackFairlineDataLoaded,
    trackEvOpportunityViewed,
    
    // Tap to Bet
    trackTapToBetClicked,
    trackTapToBetBlocked,
    trackTapToBetLoginRedirect,

    // Track Record
    trackTrackRecordViewed,

    // Why This Pick
    trackWhyThisPickExpanded,

    // Exit Intent
    trackExitIntentShown,
    trackExitIntentCtaClicked,

    // Homepage
    trackHomepageTrackRecordClicked
  };
}; 